import CircleIcon from "@mui/icons-material/Circle";
import {
  Autocomplete,
  Box,
  type FilterOptionsState,
  Grid,
  TextField,
} from "@mui/material";
import type React from "react";
import { type Dispatch, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { labToCss } from "service/color-math";
import { loadNcsColors, type NcsColor } from "../../../service/ncs-colors";
import type { NamedColor } from "./ColorFilter";

export interface ColorFilterNcsProps {
  onChange: Dispatch<NamedColor | undefined>;
  value?: NamedColor;
}

export const ColorFilterNcs = (props: ColorFilterNcsProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<Array<NcsColor>>([]);
  const isStartLoading = open && options.length === 0;

  const cleanForSearch = (s: string) =>
    s.replaceAll(/[\s-]/g, "").toLowerCase();
  const searchOptionValues = options.map((o) => cleanForSearch(o.name));

  useEffect(() => {
    if (isStartLoading) {
      loadNcsColors().then(setOptions);
    }
  }, [isStartLoading]);

  const onChange = (_event: React.SyntheticEvent, option: NcsColor | null) => {
    const value: NamedColor | undefined = option
      ? {
          value: option.lab,
          name: option.name,
        }
      : undefined;
    props.onChange(value);
  };

  function filterOptions(
    options: Array<NcsColor>,
    state: FilterOptionsState<NcsColor>,
  ) {
    const input = cleanForSearch(state.inputValue).replace(/^ncs/, "");
    return searchOptionValues
      .map((o, idx) => (o.includes(input) ? idx : -1))
      .filter((idx) => idx >= 0)
      .map((idx) => options[idx]);
  }

  return (
    <Grid size={{ xs: 12, md: 6, xl: 3 }}>
      <Autocomplete
        value={
          props.value
            ? { name: props.value.name, lab: props.value.value }
            : null
        }
        isOptionEqualToValue={(o, v) => o.name === v.name}
        onChange={onChange}
        onOpen={() => {
          setOpen(true);
        }}
        onClose={() => {
          setOpen(false);
        }}
        filterOptions={filterOptions}
        loading={isStartLoading}
        options={options}
        fullWidth
        getOptionLabel={(option) => option.name}
        renderInput={(params) => (
          <TextField {...params} label={t("filter_color_code_ncs")} />
        )}
        renderOption={(props, option) => (
          <Box component="li" {...props}>
            <CircleIcon
              sx={{ color: labToCss(option.lab), marginRight: "10px" }}
            />
            {option.name}
          </Box>
        )}
      />
    </Grid>
  );
};
